import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { createKeyboardNav } from "@/lib/keyboardNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Printer, FileText, ShoppingCart, PackagePlus, Recycle, ClipboardList, ArrowLeft, Undo2, PackageMinus, Minimize2 } from "lucide-react";
import { useMinimized } from "@/context/MinimizedContext";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import PurchaseTransactions from "@/pages/PurchaseTransactions";
import SaleTransactions from "@/pages/SaleTransactions";
import { ItemCombobox } from "@/components/ItemCombobox";
import { PaymentPanel, openTxnPdf } from "@/components/PaymentPanel";
import { ItemRegisterView } from "@/components/ItemRegisterView";
import ReturnForm from "@/components/ReturnForm";
import { useI18n } from "@/context/I18nContext";
import { PaginationBar, usePagination } from "@/components/Pagination";

const TXN_META = {
  retail_sale:       { label: "Retail Sale",       group: "Sale",     party: "customer", stock: -1, icon: ShoppingCart },
  wholesale_sale:    { label: "Wholesale Sale",    group: "Sale",     party: "customer", stock: -1, icon: ShoppingCart },
  estimate:          { label: "Estimate",          group: "Sale",     party: "customer", stock:  0, icon: FileText },
  wholesale_purchase:{ label: "Wholesale Purchase",group: "Purchase", party: "vendor",   stock: +1, icon: PackagePlus },
  retail_purchase:   { label: "Reorder",           group: "Purchase", party: "vendor",   stock: +1, icon: PackagePlus },
  purchase_order:    { label: "Purchase Order",    group: "PO",       party: "vendor",   stock:  0, icon: ClipboardList },
  scrap_receive:     { label: "Scrap Receive",     group: "Scrap",    party: "customer", stock: +1, icon: Recycle },
  scrap_issue:       { label: "Scrap Issue",       group: "Scrap",    party: "vendor",   stock: -1, icon: Recycle },
};

const RETURN_META = {
  sale_return:     { label: "Sale Return",     group: "Return", stock: +1, icon: Undo2,        mode: "sale",     roles: ["owner", "manager", "accountant"] },
  purchase_return: { label: "Purchase Return", group: "Return", stock: -1, icon: PackageMinus, mode: "purchase", roles: ["owner", "manager", "accountant"] },
};

const emptyLine = { item_id: "", qty: 1, rate: 0, discount: 0, cgst: 9, sgst: 9, serial_number: "" };
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Transactions() {
  const { user } = useAuth();
  const { settings: appSettings } = useI18n();
  const { minimize, consumeRestore, pending } = useMinimized();
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "retail_sale";
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ party_id: "", party_name: "", party_phone: "", store_id: "", lines: [{ ...emptyLine }], overall_discount: 0, overall_discount_type: "amt", notes: "" });
  const formRef = useRef(form); formRef.current = form;
  const [stores, setStores] = useState([]);
  const [printTxn, setPrintTxn] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [regItemId, setRegItemId] = useState(null);
  const [savedDoc, setSavedDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const canModify = ["owner", "manager"].includes(user.role);
  const isReturnType = type === "sale_return" || type === "purchase_return";
  const meta = TXN_META[type] || RETURN_META[type];

  const load = () => api.get(`/transactions?type=${type}`).then(r => setList(r.data));
  useEffect(() => {
    if (!isReturnType) load();
    api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
    api.get("/vendors").then(r => setVendors(r.data)).catch(() => {});
    api.get("/stores").then(r => setStores(r.data.filter(s => s.status === "Active"))).catch(() => {});
  }, [type]);

  const isPurchase = meta?.group === "Purchase" || meta?.group === "PO";
  const pickItem = (idx, itemId) => {
    const it = items.find(x => x.id === itemId);
    setForm(f => {
      const lines = [...f.lines];
      const defaultRate = it ? (Number(it[form.rate_type]) || (isPurchase && it.per_unit_cost ? it.per_unit_cost : it.mrp) || 0) : 0;
      lines[idx] = { ...lines[idx], item_id: itemId,
        rate: defaultRate, cgst: it ? it.cgst : 9, sgst: it ? it.sgst : 9 };
      return { ...f, lines };
    });
  };
  const setLine = (idx, k, v) => setForm(f => {
    const lines = [...f.lines]; lines[idx] = { ...lines[idx], [k]: v };
    return { ...f, lines };
  });
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const delLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  // Mobile Number search: exact phone match in Customer/Supplier Master auto-selects
  // the party; no match keeps the manually typed number.
  const onMobileChange = (v) => {
    const digits = v.replace(/[^\d+]/g, "");
    const m = parties.find(c => (c.phone || "") === digits);
    if (m) { setForm(f => ({ ...f, party_id: m.id, party_name: m.name || "", party_phone: m.phone || "", party_address: m.address || "" })); return; }
    setForm(f => {
      const sel = parties.find(c => c.id === f.party_id);
      return { ...f, party_phone: digits, ...(sel && (sel.phone || "") !== digits ? { party_id: "" } : {}) };
    });
  };

  // Rate Master rate-type: re-price every item line from the chosen rate; lines stay editable.
  const applyRateType = (rt) => {
    setForm(f => ({
      ...f, rate_type: rt,
      lines: f.lines.map(l => {
        if (!l.item_id) return l;
        const it = items.find(x => x.id === l.item_id);
        if (!it) return l;
        return { ...l, rate: Number(it[rt]) || Number(it.mrp) || Number(it.per_unit_cost) || 0 };
      }),
    }));
  };

  const totals = useMemo(() => {
    let net = 0, cgst = 0, sgst = 0;
    form.lines.forEach(l => {
      const it = items.find(x => x.id === l.item_id);
      const rate = Number(l.rate) || 0;
      const qty = Number(l.qty) || 0;
      const disc = Number(l.discount) || 0;
      const incl = Math.max(0, rate * qty - disc);
      const taxPct = form.tax_type === "GST" ? (Number(l.cgst) || 0) + (Number(l.sgst) || 0) : form.tax_type === "IGST" ? (Number(l.cgst) || 0) : 0;
      const n = form.bill_calc === "Inclusive" && taxPct > 0 ? round2(incl / (1 + taxPct / 100)) : incl;
      let c = 0, s = 0;
      if (form.tax_type === "GST") { c = n * (Number(l.cgst) || 0) / 100; s = n * (Number(l.sgst) || 0) / 100; }
      else if (form.tax_type === "IGST") { c = n * (Number(l.cgst) || 0) / 100; }
      net += n; cgst += c; sgst += s;
    });
    const pre = net + cgst + sgst;
    const od = Number(form.overall_discount) || 0;
    const effDisc = form.overall_discount_type === "pct" ? round2(pre * od / 100) : od;
    return { net: round2(net), cgst: round2(cgst), sgst: round2(sgst), effDisc, total: round2(pre - effDisc) };
  }, [form.lines, form.overall_discount, form.overall_discount_type, form.tax_type, form.bill_calc, items]);

  const defaultStore = () => {
    if (user.assigned_store && stores.some(s => s.id === user.assigned_store)) return user.assigned_store;
    return stores[0]?.id || "";
  };
  const openNew = () => {
    setEditId(null);
    setForm({ party_id: "", party_name: "", party_phone: "", party_address: "", store_id: defaultStore(), txn_date: todayISO(), rate_type: isPurchase ? "purchase_rate" : "mrp", invoice_number: "", tax_type: "GST", bill_calc: "Exclusive", lines: [{ ...emptyLine }], overall_discount: 0, overall_discount_type: "amt", notes: "" });
    setOpen(true);
  };
  // Deep-link edit: ?edit=<id> opens the form pre-filled (used by Transaction Master)
  useEffect(() => {
    if (isReturnType) return;
    const eid = params.get("edit");
    if (!eid) return;
    (async () => {
      try { const res = await api.get(`/transactions/${eid}`); openEdit(res.data); }
      catch (e) { toast.error(formatErr(e)); }
      const p = new URLSearchParams(params); p.delete("edit");
      setParams(p, { replace: true });
    })();
  }, [params]); // eslint-disable-line
  // Restore minimised transaction form when user clicks it in the taskbar.
  useEffect(() => {
    const p = consumeRestore("txn");
    if (!p) return;
    if (p.type && p.type !== type) {
      // Switch to the right type first — form state will be hydrated on next render.
      const nav = new URLSearchParams(params); nav.set("type", p.type);
      setParams(nav, { replace: true });
    }
    setEditId(p.editId || null);
    setForm(p.form);
    setOpen(true);
  }, [pending]); // eslint-disable-line

  const refOfTxn = () => {
    if (editId) return form.invoice_number || "Edit";
    const party = form.party_name || (customers.find(c => c.id === form.party_id)?.name) || (vendors.find(v => v.id === form.party_id)?.name);
    return party ? `For: ${party}` : "New";
  };
  const isDirty = () => {
    const l0 = form.lines?.[0];
    return !!(form.party_id || form.party_name || form.invoice_number || form.notes || (l0 && l0.item_id));
  };
  const minimise = () => {
    const label = `${editId ? "Edit" : "New"} ${meta?.label || "Transaction"}`;
    const path = `/transactions?type=${type}`;
    minimize({ kind: "txn", label, ref: refOfTxn(), state: { type, form, editId }, path, dirty: isDirty() });
    setOpen(false);
    toast.success("Minimised to taskbar");
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      party_id: t.party_id || "", party_name: t.party_name || "", party_phone: t.party_phone || "",
      txn_date: t.txn_date || todayISO(), rate_type: t.rate_type || (isPurchase ? "purchase_rate" : "mrp"),
      invoice_number: t.invoice_number || "", tax_type: t.tax_type || "GST",
      party_address: t.party_address || "", bill_calc: t.bill_calc || "Exclusive",
      store_id: t.store_id || "",
      lines: t.lines.map(l => ({ item_id: l.item_id, qty: l.qty, rate: l.rate, discount: l.discount, cgst: l.cgst, sgst: l.sgst })),
      overall_discount: t.overall_discount || 0, overall_discount_type: t.overall_discount_type || "amt", notes: t.notes || "",
    });
    setOpen(true);
  };
  const save = () => {
    if (form.lines.some(l => !l.item_id)) { toast.error("Pick items in every line"); return; }
    if (appSettings?.auto_payment_panel === false) confirmSave([]);
    else setPayOpen(true);
  };
  const confirmSave = async (payRows) => {
    setSaving(true);
    try {
      const payload = { type, party_type: meta.party, ...form,
        lines: form.lines.map(l => ({ ...l, qty: Number(l.qty), rate: Number(l.rate), discount: Number(l.discount), cgst: Number(l.cgst), sgst: Number(l.sgst) })),
        overall_discount: Number(form.overall_discount) || 0, overall_discount_type: form.overall_discount_type,
        party_id: form.party_id || null, party_name: form.party_name || null, party_phone: form.party_phone || null,
        store_id: form.store_id || null,
      };
      let tid = editId, saved = null;
      if (editId) { const r = await api.patch(`/transactions/${editId}`, payload); saved = r.data; }
      else { const r = await api.post("/transactions", payload); saved = r.data; tid = r.data.id; }
      for (const p of payRows) await api.post(`/transactions/${tid}/payment`, { amount: Number(p.amount), method: p.mode });
      toast.success(editId ? "Updated" : "Created");
      setPayOpen(false); setOpen(false); load();
      if (tid) { setSavedDoc({ id: tid, number: saved?.invoice_number || saved?.txn_number || "" }); if (appSettings?.auto_print_after_save) openTxnPdf(tid); }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!confirm("Delete this transaction? Stock will be reversed.")) return;
    try { await api.delete(`/transactions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const [childFormOpen, setChildFormOpen] = useState(false);
  const formActive = open || childFormOpen;
  useEffect(() => {
    document.body.classList.toggle("txn-form-fullscreen", formActive);
    return () => document.body.classList.remove("txn-form-fullscreen");
  }, [formActive]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const container = document.querySelector('[data-testid="txn-form-page"]');
      const first = container?.querySelector('[role="combobox"], input:not([disabled])');
      if (first) first.focus();
    }, 120);
    return () => clearTimeout(t);
  }, [open]);

  const parties = meta.party === "vendor" ? vendors : customers;

  const isPurchaseType = type === "retail_purchase" || type === "wholesale_purchase";
  const isSaleType = type === "retail_sale" || type === "wholesale_sale";

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.altKey && (e.key === "4" || e.code === "Digit4")) {
        e.preventDefault();
        const lineEl = e.target && e.target.closest ? e.target.closest('[data-testid^="line-"]') : null;
        const lm = lineEl ? (lineEl.getAttribute("data-testid") || "").match(/(\d+)$/) : null;
        if (!lm) return;
        const iid = formRef.current.lines[Number(lm[1])]?.item_id;
        if (!iid) { toast.error("Pick an item in this line first"); return; }
        setRegItemId(iid);
        return;
      }
      if (regItemId) { if (e.key === "Escape") { e.preventDefault(); setRegItemId(null); } return; }
      if (payOpen) {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); document.querySelector('[data-testid="pay-save-btn"]:not([disabled])')?.click(); }
        return;
      }
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        document.querySelector('[data-testid="save-txn-btn"]:not([disabled])')?.click();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, payOpen, regItemId]);

  const handleEnterNav = createKeyboardNav({
    selector: 'input:not([disabled]), [role="combobox"], button[data-testid="add-line-btn"], [data-net-amount]',
    endTarget: true,
    skipInside: '[data-testid="payment-panel"], [data-testid="item-register-overlay"]',
    addLineBtn: true,
  });

  return (
    <div className="p-8">
      {!formActive && (<>
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Transactions</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{meta.label}</h1>
        {meta.stock !== 0 && <div className="text-xs text-muted-foreground mt-2">This type {meta.stock > 0 ? "adds to" : "reduces"} inventory stock automatically.</div>}
      </div>

      <div className="flex gap-1.5 mb-6" data-testid="txn-tabs-row">
        {Object.entries(TXN_META).filter(() => user.role !== "accountant").map(([k, m]) => {
          const Ico = m.icon;
          return (
            <button
              key={k} data-testid={`tab-${k}`}
              onClick={() => setParams({ type: k })}
              className={`border rounded-sm p-2 text-left transition-colors flex-1 min-w-0 ${
                type === k ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:border-primary/40"
              }`}
            >
              <Ico className="w-3.5 h-3.5 mb-0.5"/>
              <div className="text-[9px] uppercase tracking-normal text-muted-foreground truncate">{m.group}</div>
              <div className="text-[11px] font-semibold truncate">{m.label}</div>
            </button>
          );
        })}
        {Object.entries(RETURN_META).filter(([, m]) => m.roles.includes(user.role)).map(([k, m]) => {
          const Ico = m.icon;
          return (
            <button
              key={k} data-testid={`tab-${k}`}
              onClick={() => setParams({ type: k })}
              className={`border rounded-sm p-2 text-left transition-colors flex-1 min-w-0 ${
                type === k ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:border-primary/40"
              }`}
            >
              <Ico className="w-3.5 h-3.5 mb-0.5"/>
              <div className="text-[9px] uppercase tracking-normal text-muted-foreground truncate">{m.group}</div>
              <div className="text-[11px] font-semibold truncate">{m.label}</div>
            </button>
          );
        })}
      </div>
      </>)}

      {isReturnType ? <ReturnForm key={type} mode={RETURN_META[type].mode} embedded onFullScreenChange={setChildFormOpen} /> : isPurchaseType ? <PurchaseTransactions key={type} type={type} label={meta.label} onFullScreenChange={setChildFormOpen} /> : isSaleType ? <SaleTransactions key={type} type={type} label={meta.label} onFullScreenChange={setChildFormOpen} /> : (
      <>
      {!open && (<>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">{list.length} record(s)</div>
        {canModify && (
          <Button data-testid="new-txn-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
            <Plus className="w-4 h-4 mr-1"/> New {meta.label}
          </Button>
        )}
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Number</th>
              <th className="text-left p-3">Party</th>
              <th className="text-right p-3">Items</th>
              <th className="text-right p-3">Total</th>
              <th className="text-left p-3">Date</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No {meta.label.toLowerCase()} records yet</td></tr>}
            {pg.pageItems.map(t => (
              <tr key={t.id} data-testid={`txn-${t.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary uppercase">{t.txn_number}</td>
                <td className="p-3">{t.party_name || "-"}<span className="text-xs text-muted-foreground ml-1">{t.party_phone || ""}</span></td>
                <td className="p-3 text-right font-mono text-xs">{t.lines.length}</td>
                <td className="p-3 text-right font-mono">{inr(t.total)}</td>
                <td className="p-3 text-muted-foreground text-xs font-mono">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right space-x-1">
                  <Button data-testid={`print-${t.id}`} size="sm" variant="ghost" onClick={() => setPrintTxn(t)} className="h-7 text-xs"><Printer className="w-3 h-3 mr-1"/> Print</Button>
                  {canModify && (
                    <>
                      <Button data-testid={`edit-${t.id}`} size="sm" variant="ghost" onClick={() => openEdit(t)} className="h-7 text-xs"><Pencil className="w-3 h-3 mr-1"/> Edit</Button>
                      <Button data-testid={`del-${t.id}`} size="sm" variant="ghost" onClick={() => del(t.id)} className="h-7 text-xs text-destructive"><Trash2 className="w-3 h-3 mr-1"/> Del</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
      </>)}

      {/* Full-page form */}
      {open && (
      <div className="border border-border bg-card rounded-sm p-6 min-h-[70vh]" data-testid="txn-form-page" onKeyDownCapture={handleEnterNav}>
          <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
            <div className="flex items-center gap-3">
              <Button type="button" data-testid="form-back-btn" variant="outline" onClick={() => setOpen(false)} className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/>Back to List</Button>
              <h2 className="font-display uppercase text-lg">{editId ? "Modify" : "New"} {meta.label}</h2>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" data-testid="minimise-txn-btn" variant="outline" onClick={minimise} className="rounded-sm text-xs uppercase tracking-wider"><Minimize2 className="w-3 h-3 mr-1"/>Minimise</Button>
              <Button type="button" data-testid="form-cancel-btn" variant="ghost" onClick={() => setOpen(false)} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground">Cancel</Button>
            </div>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-[minmax(0,2.2fr)_repeat(3,minmax(0,1fr))] gap-3">
              {/* Line 1: Party (wide), Mobile Number, Date, Invoice Number (compact) */}
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{meta.party === "vendor" ? "Supplier" : "Customer"}</Label>
                <Select value={form.party_id} onValueChange={(v) => {
                  const p = parties.find(x => x.id === v);
                  setForm(f => ({ ...f, party_id: v, party_name: p?.name || "", party_phone: p?.phone || "", party_address: p?.address || "" }));
                }}>
                  <SelectTrigger data-testid="txn-party" className="mt-1 rounded-sm bg-secondary border-border">
                    <SelectValue placeholder={`Select ${meta.party}${meta.party === "customer" ? " (or walk-in)" : ""}`}/>
                  </SelectTrigger>
                  <SelectContent>{parties.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.phone}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="txn-walkin-name" value={form.party_name} onChange={(e) => setForm(f => ({ ...f, party_id: "", party_name: e.target.value }))} placeholder="Or walk-in name" className="mt-1.5 rounded-sm bg-secondary border-border h-8 text-xs"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile Number</Label>
                <Input data-testid="txn-mobile" value={form.party_phone} onChange={(e) => onMobileChange(e.target.value)} placeholder="Search by mobile…" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
                <Input type="date" data-testid="txn-date" value={form.txn_date} onChange={(e) => setForm(f => ({ ...f, txn_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Invoice Number</Label>
                <Input data-testid="txn-invoice-number" value={form.invoice_number} onChange={(e) => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="Auto-generated" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
              </div>
            </div>
            <div className="grid grid-cols-[minmax(0,1.2fr)_repeat(4,minmax(0,1fr))] gap-3">
              {/* Line 2: Address+Rate align under Party; Tax/Store/Bill Calc under Mobile/Date/Invoice */}
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                <Input data-testid="txn-address" value={form.party_address} onChange={(e) => setForm(f => ({ ...f, party_address: e.target.value }))} placeholder="Auto-filled from party" className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rate (Rate Master)</Label>
                <Select value={form.rate_type} onValueChange={applyRateType}>
                  <SelectTrigger data-testid="txn-rate-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mrp">MRP</SelectItem>
                    <SelectItem value="wholesale_rate">Wholesale Rate</SelectItem>
                    <SelectItem value="purchase_rate">Purchase Rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Tax Type</Label>
                <Select value={form.tax_type} onValueChange={(v) => setForm(f => ({ ...f, tax_type: v }))}>
                  <SelectTrigger data-testid="txn-tax-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GST">GST (CGST + SGST)</SelectItem>
                    <SelectItem value="IGST">IGST</SelectItem>
                    <SelectItem value="Exempt">Exempt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Store</Label>
                <Select value={form.store_id} onValueChange={(v) => setForm(f => ({ ...f, store_id: v }))}>
                  <SelectTrigger data-testid="txn-store" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select store"/></SelectTrigger>
                  <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {s.store_type}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Bill Calculation</Label>
                <Select value={form.bill_calc} onValueChange={(v) => setForm(f => ({ ...f, bill_calc: v }))}>
                  <SelectTrigger data-testid="txn-bill-calc" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Exclusive">Exclusive of Tax</SelectItem>
                    <SelectItem value="Inclusive">Inclusive of Tax</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border border-border rounded-sm">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <tr>
                    <th className="text-center p-2">Item (from Master)</th>
                    <th className="text-center p-2 w-28">Part No</th>
                    <th className="text-center p-2 w-24">HSN</th>
                    <th className="text-center p-2 w-16">Qty</th>
                    <th className="text-center p-2 w-24">Rate</th>
                    <th className="text-center p-2 w-20">Disc</th>
                    <th className="text-center p-2 w-16">CGST</th>
                    <th className="text-center p-2 w-16">SGST</th>
                    <th className="text-center p-2 w-24">Total</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((l, i) => {
                    const it = items.find(x => x.id === l.item_id);
                    const net = Math.max(0, (Number(l.rate) || 0) * (Number(l.qty) || 0) - (Number(l.discount) || 0));
                    const total = net * (1 + (Number(l.cgst) || 0)/100 + (Number(l.sgst) || 0)/100);
                    return (
                      <tr key={i} data-testid={`line-${i}`} className="border-t border-border">
                        <td className="p-1">
                          <ItemCombobox
                            items={items} value={l.item_id} testId={`line-item-${i}`} triggerClass="h-8"
                            labelFn={(x) => `${x.item_code ? x.item_code + " · " : ""}${x.item_name} · ${isPurchase && x.per_unit_cost ? `Cost ₹${x.per_unit_cost}` : `MRP ₹${x.mrp}`}`}
                            onSelect={(x) => pickItem(i, x.id)}
                            afterSelect={() => setTimeout(() => document.querySelector(`[data-testid="line-qty-${i}"]`)?.focus(), 50)}
                          />
                          {it?.warranty === "Yes" && (
                            <Input data-testid={`line-serial-${i}`} value={l.serial_number || ""} onChange={(e) => setLine(i, "serial_number", e.target.value)} placeholder="Serial # (warranty)" className="mt-1 h-7 rounded-sm bg-secondary border-border text-xs font-mono"/>
                          )}
                        </td>
                        <td className="p-1"><Input data-testid={`line-partno-${i}`} value={it?.part_number || ""} readOnly className="h-8 rounded-sm bg-secondary border-border text-xs font-mono text-muted-foreground"/></td>
                        <td className="p-1"><Input data-testid={`line-hsn-${i}`} value={it?.hsn_code || ""} readOnly className="h-8 rounded-sm bg-secondary border-border text-xs font-mono text-muted-foreground"/></td>
                        <td className="p-1"><Input data-testid={`line-qty-${i}`} type="number" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                        <td className="p-1"><Input data-testid={`line-rate-${i}`} type="number" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                        <td className="p-1"><Input type="number" value={l.discount} onChange={(e) => setLine(i, "discount", e.target.value)} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                        <td className="p-1"><Input type="number" value={l.cgst} onChange={(e) => setLine(i, "cgst", e.target.value)} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                        <td className="p-1"><Input type="number" value={l.sgst} onChange={(e) => setLine(i, "sgst", e.target.value)} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                        <td className="p-1 text-right font-mono text-xs">{inr(round2(total))}</td>
                        <td className="p-1"><Button size="sm" variant="ghost" onClick={() => delLine(i)} disabled={form.lines.length === 1} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="p-2 border-t border-border">
                <Button data-testid="add-line-btn" size="sm" variant="outline" onClick={addLine} className="rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/> Add Line</Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes</Label>
                <Textarea data-testid="txn-notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={3}/>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Net Amount</span><span className="font-mono" data-testid="totals-net">{inr(totals.net)}</span></div>
                {form.tax_type === "IGST" ? (
                  <div className="flex justify-between text-muted-foreground"><span>IGST</span><span className="font-mono" data-testid="totals-cgst">{inr(totals.cgst)}</span></div>
                ) : form.tax_type !== "Exempt" && (
                  <>
                    <div className="flex justify-between text-muted-foreground"><span>CGST</span><span className="font-mono" data-testid="totals-cgst">{inr(totals.cgst)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>SGST</span><span className="font-mono" data-testid="totals-sgst">{inr(totals.sgst)}</span></div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Overall Discount</span>
                  <div className="flex items-center gap-1">
                    {form.overall_discount_type === "pct" && Number(form.overall_discount) > 0 && <span data-testid="overall-discount-amt" className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">= {inr(totals.effDisc)}</span>}
                    <Input data-testid="overall-disc" data-end-target type="number" min="0" {...(form.overall_discount_type === "pct" ? { max: "100" } : {})} value={form.overall_discount} onChange={(e) => setForm(f => ({ ...f, overall_discount: e.target.value }))} className="w-24 h-8 rounded-sm bg-secondary border-border text-right"/>
                    <div className="flex border border-border rounded-sm overflow-hidden shrink-0">
                      <button type="button" data-testid="disc-type-amt" onClick={() => setForm(f => ({ ...f, overall_discount_type: "amt" }))} className={`px-2 h-8 text-xs ${form.overall_discount_type === "amt" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>₹</button>
                      <button type="button" data-testid="disc-type-pct" onClick={() => setForm(f => ({ ...f, overall_discount_type: "pct" }))} className={`px-2 h-8 text-xs ${form.overall_discount_type === "pct" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>%</button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between border-t border-border pt-2 items-center">
                  <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Grand Total</span>
                  <span data-testid="totals-grand" data-net-amount tabIndex={-1} className="font-display font-black text-2xl text-primary rounded-sm px-1 focus:outline-none focus:ring-2 focus:ring-primary">{inr(totals.total)}</span>
                </div>
              </div>
            </div>

            <Button data-testid="save-txn-btn" onClick={save} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
              {editId ? "Update Transaction" : `Save ${meta.label}`}
            </Button>
          </div>
      </div>
      )}

      {regItemId && (
        <div className="fixed inset-0 z-50 bg-background overflow-y-auto p-6" data-testid="item-register-overlay">
          <ItemRegisterView itemId={regItemId} onClose={() => setRegItemId(null)} />
        </div>
      )}

      <PaymentPanel open={payOpen} title={meta.label} grandTotal={totals.total} saving={saving}
        onCancel={() => { setPayOpen(false); setTimeout(() => document.querySelector('[data-testid="save-txn-btn"]')?.focus(), 60); }}
        onConfirm={confirmSave} />

      <Dialog open={!!savedDoc} onOpenChange={(v) => { if (!v) setSavedDoc(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-sm text-center">
          <DialogHeader><DialogTitle className="font-display uppercase text-center">Save Confirmation</DialogTitle></DialogHeader>
          {savedDoc && (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Invoice Number</div>
                <div className="font-mono font-bold text-xl text-primary mt-1" data-testid="saved-invoice-number">{savedDoc.number}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="print-invoice-btn" onClick={() => openTxnPdf(savedDoc.id)} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Printer className="w-3 h-3 mr-1"/>Print Invoice</Button>
                <Button data-testid="new-transaction-btn" variant="outline" onClick={() => { setSavedDoc(null); openNew(); }} className="rounded-sm uppercase tracking-[0.15em] text-xs"><Plus className="w-3 h-3 mr-1"/>New Transaction</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print dialog */}
      <Dialog open={!!printTxn} onOpenChange={(v) => { if (!v) setPrintTxn(null); }}>
        <DialogContent className="rounded-sm bg-white text-black max-w-2xl max-h-[90vh] overflow-y-auto print:shadow-none">
          {printTxn && (
            <div id="print-bill" className="p-4">
              <div className="border-b-2 border-black pb-2 mb-3 flex justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest">SynqMoto ERP Workshop</div>
                  <div className="text-xl font-bold">{TXN_META[printTxn.type].label.toUpperCase()}</div>
                </div>
                <div className="text-right text-xs">
                  <div>#{printTxn.txn_number}</div>
                  <div>{new Date(printTxn.created_at).toLocaleString()}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 text-xs mb-3">
                <div><strong>{TXN_META[printTxn.type].party === "vendor" ? "Vendor" : "Customer"}:</strong> {printTxn.party_name || "Walk-in"}</div>
                <div><strong>Phone:</strong> {printTxn.party_phone || "-"}</div>
              </div>
              <table className="w-full text-xs mb-3 border-t border-b border-black">
                <thead>
                  <tr className="border-b border-black">
                    <th className="text-left py-1">Item</th><th>HSN</th><th className="text-right">Qty</th>
                    <th className="text-right">Rate</th><th className="text-right">Disc</th>
                    <th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {printTxn.lines.map((l, i) => (
                    <tr key={i} className="border-b border-gray-300">
                      <td className="py-1">{l.name} <span className="text-gray-500">({l.part_number})</span></td>
                      <td className="text-center">{l.hsn_code}</td>
                      <td className="text-right">{l.qty} {l.unit}</td>
                      <td className="text-right">₹{l.rate}</td>
                      <td className="text-right">₹{l.discount}</td>
                      <td className="text-right">₹{l.cgst_amt}</td>
                      <td className="text-right">₹{l.sgst_amt}</td>
                      <td className="text-right font-bold">₹{l.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="ml-auto max-w-xs text-xs space-y-1">
                <div className="flex justify-between"><span>Net</span><span>₹{printTxn.subtotal}</span></div>
                <div className="flex justify-between"><span>CGST</span><span>₹{printTxn.cgst_total}</span></div>
                <div className="flex justify-between"><span>SGST</span><span>₹{printTxn.sgst_total}</span></div>
                {printTxn.overall_discount > 0 && <div className="flex justify-between"><span>Discount</span><span>-₹{printTxn.overall_discount}</span></div>}
                <div className="flex justify-between border-t border-black pt-1 text-base font-bold"><span>Grand Total</span><span>₹{printTxn.total}</span></div>
              </div>
              {printTxn.notes && <div className="mt-3 text-xs"><strong>Notes:</strong> {printTxn.notes}</div>}
              <div className="mt-8 grid grid-cols-2 gap-8 text-xs">
                <div className="border-t border-black pt-1 text-center">Authorised Signatory</div>
                <div className="border-t border-black pt-1 text-center">Recipient Signature</div>
              </div>
              <div className="mt-3 print:hidden flex justify-end">
                <Button onClick={() => window.print()} className="rounded-sm bg-primary text-primary-foreground text-xs"><Printer className="w-3 h-3 mr-1"/> Print</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body > *:not(:has(#print-bill)) { display: none !important; }
          [role="dialog"] { position: static !important; box-shadow: none !important; }
        }
      `}</style>
      </>
      )}
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }
